import type { PostResumo } from "@/lib/blog.functions";
// Portado de /tmp/astro/src/pages/tratamentos/medicina-do-esporte.astro (site Astro).
import { Hero } from "@/components/itv/tratamento/Hero";
import { Pilares } from "@/components/itv/tratamento/Pilares";
import { Dores } from "@/components/itv/tratamento/Dores";
import { CardsIlustrado } from "@/components/itv/tratamento/CardsIlustrado";
import { Medico } from "@/components/itv/tratamento/Medico";
import { Faq } from "@/components/itv/tratamento/Faq";
import { Conteudos } from "@/components/itv/tratamento/Conteudos";
import { CtaPratico } from "@/components/itv/tratamento/CtaPratico";

const pilares = [
  { icone: "/icones/search.svg", titulo: "Do iniciante ao atleta", texto: "Não é preciso competir. Quem treina, ou quer começar a treinar, se beneficia da avaliação." },
  { icone: "/icones/shield.svg", titulo: "Dor avaliada na mesma consulta", texto: "O registro em Ortopedia (RQE 113822) permite avaliar a dor e o gesto no mesmo consultório." },
  { icone: "/icones/video-cam.svg", titulo: "Onde e como", texto: "Presencial em Bauru ou por teleconsulta para todo o Brasil. Atendimento particular.", link: { href: "/medico-do-esporte-em-bauru/", rotulo: "Atendimento presencial em Bauru" } },
];

const situacoes = [
  { icone: "/icones/flame.svg", titulo: "Dor que acompanha o treino", texto: "Ombro no supino, joelho no agachamento ou na corrida. Dores que a pessoa vem adiando avaliar." },
  { icone: "/icones/moon.svg", titulo: "Treino estagnado", texto: "Meses sem evolução de carga ou de composição corporal. Nutrição, sono e recuperação explicam boa parte dos casos." },
  { icone: "/icones/activity.svg", titulo: "Retorno após lesão", texto: "Voltar a treinar com segurança pede plano progressivo e acompanhamento." },
  { icone: "/icones/clipboard.svg", titulo: "Começando depois dos 40", texto: "Avaliação completa antes de aumentar a intensidade, para treinar forte com segurança." },
];

const frentes = [
  { img: "/img/esporte/conduta-1.webp", titulo: "Nutrição para o treino", texto: "Energia e proteína ajustadas ao volume e ao objetivo." },
  { img: "/img/esporte/conduta-2.webp", titulo: "Suplementação com evidência", texto: "Creatina e o que mais tiver evidência para o seu caso." },
  { img: "/img/esporte/conduta-3.webp", titulo: "Recuperação e sono", texto: "Parte do rendimento acontece fora do treino." },
  { img: "/img/esporte/conduta-4.webp", titulo: "Avaliação da dor", texto: "Ombro, joelho e lesões de treino, com a leitura ortopédica do próprio médico." },
  { img: "/img/esporte/conduta-5.webp", titulo: "Plano de retorno", texto: "Progressão de carga monitorada para voltar de lesão sem recair." },
  { img: "/img/esporte/conduta-6.webp", titulo: "Check-up do esportista", texto: "Avaliação completa para começar ou intensificar o treino." },
];

// Menção a "Hospital Albert Einstein" omitida: não consta na lista de credenciais autorizadas.
const paragrafosMedico = [
  "O Dr. Thiago é médico do esporte com registro também em Ortopedia e Traumatologia (RQE 113822), membro da SBOT e da Sociedade Brasileira de Cirurgia do Ombro e Cotovelo.",
  "Na prática, a nutrição, a recuperação e a dor de treino são avaliadas pelo mesmo médico. Formação pela UNIFESP-EPM, em 2010.",
];

export const faq = [
  { pergunta: "Preciso ser atleta para me consultar?", resposta: "Não. O cuidado é para qualquer pessoa que treina ou quer começar, em qualquer nível." },
  { pergunta: "Vocês tratam lesão de treino?", resposta: "Sim. O registro em Ortopedia e Traumatologia permite avaliar dores de ombro, joelho e lesões de treino na mesma consulta, com plano de retorno progressivo." },
  { pergunta: "Posso continuar com meu personal?", resposta: "Sim. A orientação médica é feita em parceria com quem prescreve o seu treino, não no lugar dele." },
  { pergunta: "Esse acompanhamento é para mim?", resposta: "Se você treina, quer começar a treinar, está estagnado ou sente dor no treino, sim. Não é preciso competir." },
  { pergunta: "Funciona por teleconsulta?", resposta: "Sim para a avaliação nutricional, de recuperação e de performance. Quando a queixa exige exame físico da articulação, indicamos a consulta presencial." },
];


export function MedicinaDoEsporte({ artigos }: { artigos: PostResumo[] }) {
  return (
    <>
      <Hero
        trilha={[{ nome: "Home", href: "/" }, { nome: "Tratamentos", href: "/tratamentos/" }, { nome: "Medicina do esporte" }]}
        sobretitulo="TRATAMENTO"
        linha1="Medicina do Esporte"
        linha2="e Ortopedia."
        chamada="Para quem treina, do iniciante ao atleta. Performance, recuperação e as dores que aparecem com o treino, avaliadas pelo mesmo médico."
        imagem="/img/esporte/hero.webp"
      />

      <Pilares
        sobretitulo="MEDICINA DO ESPORTE"
        titulo="Saúde e performance de quem treina"
        chamada="A medicina do esporte avalia saúde, composição corporal, exames, nutrição e recuperação de quem pratica atividade física. Com o registro em Ortopedia, dores no ombro, no joelho e lesões de treino são avaliadas na mesma consulta, pelo mesmo médico."
        itens={pilares}
      />

      <Dores
        sobretitulo="QUEM PROCURA"
        titulo="Do treino estagnado à dor no ombro"
        chamada="Quatro situações frequentes no consultório:"
        itens={situacoes}
      />

      <CardsIlustrado
        sobretitulo="PERFORMANCE E DOR NO MESMO PLANO"
        titulo="Do prato ao gesto: as frentes de trabalho"
        chamada="Nutrição, recuperação e a leitura ortopédica, conforme o caso:"
        cards={frentes}
      />

      <Medico paragrafos={paragrafosMedico} />
      <Faq titulo="Dúvidas de quem treina" itens={faq} />
      <Conteudos titulo="Continue lendo sobre treino e dor" artigos={artigos} />
      <CtaPratico linha1="Comece seu tratamento de" linha2="medicina do esporte. Escolha o formato." />
    </>
  );
}
